import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface MatrixProps {
  head: string[];
  rows: string[][];
  /** Row indices rendered with the indigo accent — the rows that carry the argument. */
  accentRows?: number[];
  /** Column indices right-aligned with tabular figures. */
  numeric?: number[];
  /** Minimum table width before horizontal scrolling kicks in. */
  minWidth?: string;
  className?: string;
  /** Renders a cell's text, e.g. with inline markup. Defaults to the plain string. */
  renderCell?: (cell: string) => ReactNode;
}

/**
 * Dense comparison table for investor material.
 * Scrolls horizontally inside its own container so the page body never does.
 */
export function Matrix({
  head,
  rows,
  accentRows = [],
  numeric = [],
  minWidth = '560px',
  className,
  renderCell = (cell) => cell,
}: MatrixProps) {
  return (
    <div className={cn('overflow-x-auto', className)}>
      <table className="w-full border-collapse text-left" style={{ minWidth }}>
        <thead>
          <tr className="border-b border-slate-600/50">
            {head.map((label, i) => (
              <th
                key={label}
                className={cn(
                  'py-2.5 pr-4 text-[11px] font-semibold uppercase tracking-wider text-slate-500',
                  numeric.includes(i) && 'pr-0 text-right'
                )}
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, r) => {
            const accent = accentRows.includes(r);
            return (
              <tr
                key={r}
                className={cn(
                  'border-b border-slate-700/40 last:border-b-0',
                  accent && 'bg-indigo-500/[0.08]'
                )}
              >
                {row.map((cell, c) => (
                  <td
                    key={c}
                    className={cn(
                      'py-2.5 pr-4 align-top text-[13px] leading-relaxed',
                      c === 0 ? 'font-medium text-slate-200' : 'text-slate-400',
                      accent && c === 0 && 'text-indigo-200',
                      numeric.includes(c) && 'pr-0 text-right tabular-nums'
                    )}
                  >
                    {renderCell(cell)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
